import React from 'react';
import '../styles/pages/LegalPages.css';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <div className="legal-hero">
        <h1>Privacy Policy</h1>
        <p>Last Updated: January 30, 2026</p>
      </div>

      <div className="legal-container">
        <section className="legal-section">
          <h2>1. Introduction</h2>
          <p>
            Local Infotech Solution System ("LCS", "we", "us", or "our") is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our credit information services platform.
          </p>
          <p>
            We comply with the Digital Personal Data Protection Act, 2023 (DPDP Act), MCA guidelines, and all applicable data protection laws in India.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Personal Information</h3>
          <ul>
            <li>Full name, date of birth, gender</li>
            <li>Contact information (email, phone number, address)</li>
            <li>Government-issued identification (PAN, Aadhaar, Passport, Voter ID)</li>
            <li>Financial information (income, employment details, bank account information)</li>
          </ul>

          <h3>2.2 Organization Information</h3>
          <ul>
            <li>Organization name, registration details, GST number</li>
            <li>Business address and contact information</li>
            <li>Authorized representatives' details</li>
            <li>Banking and wallet transaction information</li>
          </ul>

          <h3>2.3 Technical Information</h3>
          <ul>
            <li>IP address, browser type, device information</li>
            <li>Usage data, log files, cookies</li>
            <li>Session information and access logs</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. How We Use Your Information</h2>
          <p>We use collected information for the following purposes:</p>
          <ul>
            <li><strong>KYC Verification:</strong> To fetch and generate  reports from authorized company</li>
            <li><strong>Identity Verification:</strong> To verify PAN, Aadhaar, and other identification documents</li>
            <li><strong>Service Delivery:</strong> To process credit checks, generate reports, and maintain user accounts</li>
            <li><strong>Compliance:</strong> To comply with MCA guidelines, KYC norms, and legal obligations</li>
            <li><strong>Communication:</strong> To send service updates, notifications, and support messages</li>
            <li><strong>Security:</strong> To detect and prevent fraud, unauthorized access, and security threats</li>
            <li><strong>Analytics:</strong> To improve our services, analyze usage patterns, and enhance user experience</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. Legal Basis for Processing</h2>
          <p>We process your personal data based on:</p>
          <ul>
            <li><strong>Consent:</strong> You have given explicit consent for KYC checks and data processing</li>
            <li><strong>Contract:</strong> Processing is necessary to perform our services under the user agreement</li>
            <li><strong>Legal Obligation:</strong> Compliance with MCA regulations, KYC norms, and anti-money laundering laws</li>
            <li><strong>Legitimate Interest:</strong> To improve services, prevent fraud, and ensure platform security</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>5. Data Sharing and Disclosure</h2>
          
          <h3>5.1 Third-Party Service Providers</h3>
          <p>We share data with authorized third parties for service delivery:</p>
          <ul>
            <li><strong>Payment Processors:</strong> For wallet top-ups and transaction processing</li>
            <li><strong>Cloud Services:</strong> AWS, Azure, or similar for secure data storage</li>
            <li><strong>Verification Services:</strong>  Government-approved agencies for PAN, Aadhaar, Court Case verification</li>
          </ul>

          <h3>5.2 Legal Requirements</h3>
          <p>We may disclose information when required by:</p>
          <ul>
            <li>Court orders, subpoenas, or legal processes</li>
            <li>Government authorities, regulatory bodies (MCA)</li>
            <li>Law enforcement agencies for investigation purposes</li>
          </ul>

          <h3>5.3 Business Transfers</h3>
          <p>In case of merger, acquisition, or sale of assets, your data may be transferred to the acquiring entity with proper notice.</p>
        </section>

        <section className="legal-section">
          <h2>6. Data Retention</h2>
          <p>We retain personal data as follows:</p>
          <ul>
            <li><strong>User Accounts:</strong>  Until account deletion request + 90 days</li>
            <li><strong>Transaction Records:</strong> 7 years (for audit and compliance)</li>
            <li><strong>KYC Documents:</strong> 10 years after relationship ends (MCA mandate)</li>
            <li><strong>Log Files:</strong> 180 days (for security and troubleshooting)</li>
          </ul>
          <p>After retention periods, data is securely deleted or anonymized.</p>
        </section>

        <section className="legal-section">
          <h2>7. Your Rights Under DPDP Act</h2>
          <p>You have the following rights regarding your personal data:</p>
          <ul>
            <li><strong>Right to Access:</strong> Request a copy of your personal data we hold</li>
            <li><strong>Right to Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Right to Erasure:</strong> Request deletion of your data (subject to legal retention requirements)</li>
            <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for data processing (may limit service access)</li>
            <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
            <li><strong>Right to Grievance Redressal:</strong> File complaints with our Data Protection Officer or Data Protection Board of India</li>
          </ul>
          <p>To exercise these rights, contact: <strong>privacy@lcsolution.in</strong></p>
        </section>

        <section className="legal-section">
          <h2>8. Data Security</h2>
          <p>We implement industry-standard security measures:</p>
          <ul>
            <li><strong>Encryption:</strong> AES-256 encryption for data at rest, TLS 1.3 for data in transit</li>
            <li><strong>Access Controls:</strong> Role-based access, multi-factor authentication (MFA)</li>
            <li><strong>Secure Infrastructure:</strong>  ISO 27001 certified cloud hosting, regular security audits</li>
            <li><strong>Monitoring:</strong> 12/7 intrusion detection, automated threat response</li>
            <li><strong>Regular Backups:</strong> Encrypted backups with disaster recovery protocols</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>9. Cookies and Tracking</h2>
          <p>We use cookies and similar technologies for:</p>
          <ul>
            <li><strong>Essential Cookies:</strong> Authentication, security, session management</li>
            <li><strong>Analytics Cookies:</strong> Google Analytics, usage tracking (anonymized)</li>
            <li><strong>Preference Cookies:</strong> Language, theme, user settings</li>
          </ul>
          <p>You can control cookies through browser settings. Disabling cookies may affect platform functionality.</p>
        </section>

        <section className="legal-section">
          <h2>10. Children's Privacy</h2>
          <p>
            Our services are not intended for individuals under 18 years of age. We do not knowingly collect data from minors. If we discover such data, it will be deleted immediately.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. International Data Transfers</h2>
          <p>
            Your data is primarily stored in India. If transferred internationally, we ensure compliance with DPDP Act requirements and use Standard Contractual Clauses (SCCs) or equivalent safeguards.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. Changes to Privacy Policy</h2>
          <p>
            We may update this Privacy Policy periodically. Material changes will be notified via email or platform notification. Continued use after changes constitutes acceptance.
          </p>
        </section>

        <section className="legal-section">
          <h2>13. Contact Information</h2>
          <div className="contact-box">
            <p><strong>Data Protection Officer</strong></p>
            <p>Email: <a href="mailto: info@lcsolution.in"> info@lcsolution.in</a></p>
            <p>Email (General): <a href="mailto: info@lcsolution.in"> info@lcsolution.in</a></p>
            <p>Address: Local Infotech Solution System, India</p>
          </div>
        </section>

        <section className="legal-section">
          <h2>14. Grievance Redressal</h2>
          <p>For complaints or concerns:</p>
          <ol>
            <li>Email our DPO at <strong>dpo@lcsolution.in</strong> (Response within 7 days)</li>
            <li>If unresolved, escalate to: <strong>info@lcsolution.in</strong></li>
            <li>File complaint with Data Protection Board of India: <a href="https://www.dataprotection.gov.in" target="_blank" rel="noopener noreferrer">www.dataprotection.gov.in</a></li>
          </ol>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
